import React, { useMemo } from "react";
import { Button, SelectPickerInput } from "@shared/components";
import { changeListeners, FieldErrorMessage, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { procedureRecurrenceOptions } from "@containers/Procedure/constants";
import { CreateProcedureDto, ProcedureFormShape } from "@containers/Procedure/interfaces";
import { Procedure, Process, Team } from "@shared/models";

import { getInitValues, handlers, prepareSubmitValue, validationSchema } from "./formHelpers";

import "./index.scss";

interface ProcedureFormProps {
  procedure: Procedure | null;
  processes: Process[];
  teams: Team[];
  onDelete: () => void;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateProcedureDto) => void;
}

interface ProcedureFormFieldsProps extends ProcedureFormProps {
  formValues: ProcedureFormShape;
}

/** The form body: owns the form instance, built once per set of initial values (see `ProcedureForm`). */
const ProcedureFormFields: React.FunctionComponent<ProcedureFormFieldsProps> = (props) => {
  const { procedure, processes, teams, formValues, onChangeForm, onCloseForm, onDelete, onSubmitForm } = props;

  const processOptions = useMemo(() => processes.map((i) => ({ label: i.name, value: i.id })), [processes]);
  const teamOptions = useMemo(() => teams.map((i) => ({ label: i.name, value: i.id })), [teams]);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(validationSchema.parse(value)));
    },
  });

  return (
    <div className="procedure-form">
      <form onSubmit={formSubmitHandler(form)}>
        <div className="procedure-form-content">
          <div className="procedure-name-container">
            <form.AppField name="name">
              {(field) => (
                <field.Input
                  className="procedure-name-input"
                  type="text"
                  labelTitle="Procedure name"
                  placeholder="Procedure name"
                />
              )}
            </form.AppField>
          </div>
          {/* The picker and its error are siblings (the error sits outside the bordered picker box), so the
              select is bound by hand instead of through `field.Select`, which renders the error inside. */}
          <form.AppField name="process">
            {(field) => (
              <div className="procedure-process-input-wrapper">
                <div className="procedure-process-input">
                  <SelectPickerInput
                    searchable={false}
                    name={field.name}
                    cleanable={false}
                    data={processOptions}
                    onChange={(value) => field.handleChange(value)}
                    labelTitle="Process Name"
                    value={field.state.value}
                    placeholder="Process Name"
                  />
                </div>
                <FieldErrorMessage />
              </div>
            )}
          </form.AppField>
          <div className="procedure-process-input-wrapper">
            <div className="procedure-process-input">
              <form.AppField name="project">
                {(field) => (
                  <field.AsyncSelect
                    labelTitle="Project Name"
                    className="project-input"
                    placeholder="Select Project"
                    getData={handlers.project.getData}
                    selectData={handlers.project.selectData}
                    prepareOptionFunction={handlers.project.prepareOptionFunction}
                    loadingAction={handlers.project.loadingAction}
                  />
                )}
              </form.AppField>
            </div>
          </div>
          <div className="teams-wrapper">
            <div className="teams-header">Accountable team</div>
            <form.AppField name="accountable_team_id">
              {(field) => (
                <>
                  <div className="teams-body">
                    {teamOptions.map((teamOption) => (
                      <label className="team-label" key={teamOption.value}>
                        <input
                          name="accountable_team"
                          type="checkbox"
                          className="team-checkbox"
                          value={teamOption.value}
                          checked={field.state.value === teamOption.value}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.handleChange(teamOption.value);
                            }
                          }}
                        />
                        <div className="team-label-text">{teamOption.label}</div>
                      </label>
                    ))}
                  </div>
                  <FieldErrorMessage />
                </>
              )}
            </form.AppField>
          </div>
          <div className="teams-wrapper">
            <div className="teams-header">Support team</div>
            <form.AppField name="teams" mode="array">
              {(field) => (
                <>
                  <div className="teams-body">
                    {teamOptions.map((teamOption) => (
                      <label className="team-label" key={teamOption.value}>
                        <input
                          name="teams"
                          type="checkbox"
                          className="team-checkbox"
                          value={teamOption.value}
                          checked={field.state.value.includes(teamOption.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.pushValue(teamOption.value);
                            } else {
                              field.removeValue(field.state.value.indexOf(teamOption.value));
                            }
                          }}
                        />
                        <div className="team-label-text">{teamOption.label}</div>
                      </label>
                    ))}
                  </div>
                  <FieldErrorMessage />
                </>
              )}
            </form.AppField>
          </div>
          <div className="procedure-link-container">
            <form.AppField name="external_link">
              {(field) => (
                <field.Textarea
                  className="procedure-link-input"
                  type="text"
                  labelTitle="Procedure Google Doc Link"
                  placeholder="Procedure Google Doc Link"
                />
              )}
            </form.AppField>
          </div>
          <form.AppField name="recurrence">
            {(field) => (
              <div className="procedure-process-input-wrapper">
                <div className="procedure-process-input">
                  <SelectPickerInput
                    searchable={false}
                    name={field.name}
                    cleanable={false}
                    data={procedureRecurrenceOptions}
                    onChange={(value) => field.handleChange(value)}
                    labelTitle="Recurrence"
                    value={field.state.value}
                    placeholder="Recurrence"
                  />
                </div>
                <FieldErrorMessage />
              </div>
            )}
          </form.AppField>
        </div>

        <div className="procedure-form-footer">
          <div className="procedure-form-footer-left">
            {procedure ? (
              <Button type="button" className="clear padding" onClick={onDelete}>
                Delete
              </Button>
            ) : null}
          </div>

          <div className="procedure-form-footer-right">
            <Button type="button" className="close-btn margin-right" onClick={onCloseForm}>
              Cancel
            </Button>
            <Button type="submit" className="accent padding">
              {procedure ? "Save" : "Add Procedure"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

/** Keyed on the procedure's initial values: a procedure that arrives or changes while the form is open mounts a fresh form. */
const ProcedureForm: React.FunctionComponent<ProcedureFormProps> = (props) => {
  const formValues = useMemo(() => getInitValues(props.procedure), [props.procedure]);

  return <ProcedureFormFields key={JSON.stringify(formValues)} formValues={formValues} {...props} />;
};

export default ProcedureForm;
